import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Card, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, TextField } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import DnsInstructions from "../components/DnsInstructions";
import DomainStatusStepper, { DomainStatusChip, formatTimestamp } from "../components/DomainStatusStepper";
import SlugField from "../components/SlugField";
import { siteHostname } from "../utils/api";
import { classifyDomain, expectedRecords, isValidDomain, normalizeDomain } from "../utils/domain";
import { domainExample, siteVertical } from "../utils/vertical";

const POLL_MS = 30_000;
const POLLING_STATUSES = ["pending_dns", "verified", "tls_pending"];

const CHECK_LABELS = {
  cname: "CNAME record",
  address: "Address (A/AAAA) records",
  txt: "Ownership TXT record",
  www: "www record",
};

function formatObserved(value) {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "nothing found";
  if (value === null || value === undefined || value === "") return "nothing found";
  return String(value);
}

function CheckResults({ checks }) {
  if (!checks?.length) return null;
  return (
    <List dense disablePadding sx={{ mt: 1 }}>
      {checks.map((check) => (
        <ListItem key={check.name} disableGutters alignItems="flex-start">
          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
            {check.ok
              ? <CheckCircleIcon fontSize="small" sx={{ color: colors.success.main }} />
              : <ErrorOutlineIcon fontSize="small" sx={{ color: colors.status.warning.dot }} />}
          </ListItemIcon>
          <ListItemText
            primary={<MDTypography variant="button" fontWeight="medium">{CHECK_LABELS[check.name] || check.name}{check.ok ? " found" : " not found yet"}</MDTypography>}
            secondary={(
              <MDTypography variant="caption" color="text" component="span" display="block">
                Expected {formatObserved(check.expected)} · found {formatObserved(check.observed)}
              </MDTypography>
            )}
          />
        </ListItem>
      ))}
    </List>
  );
}

CheckResults.propTypes = { checks: PropTypes.arrayOf(PropTypes.object) };
CheckResults.defaultProps = { checks: [] };

function DomainTab({ site, domain, saving, setDomain, verifyDomain, removeDomain, refreshDomain, patch, checkSlug }) {
  const exampleDomain = domainExample(siteVertical(site));
  const [draftDomain, setDraftDomain] = useState("");
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [slug, setSlug] = useState(site.settings?.platform_slug || "");
  const [slugStatus, setSlugStatus] = useState({ state: "idle" });

  const currentSlug = site.settings?.platform_slug || "";
  const checkSlugForSite = useCallback((value) => checkSlug(value, site.id), [checkSlug, site.id]);
  const platformHost = siteHostname(site, domain);
  const legacyHost = domain?.legacy_hostname || site.settings?.legacy_dns_target || "";
  const connected = Boolean(domain?.domain) && domain?.status !== "none";
  const status = domain?.status || "none";

  useEffect(() => {
    setSlug(site.settings?.platform_slug || "");
  }, [site.id, site.settings?.platform_slug]);

  useEffect(() => {
    setEditing(false);
    setConfirmRemove(false);
    setDraftDomain("");
  }, [site.id]);

  // Keep the status fresh while the backend poller moves the domain along.
  useEffect(() => {
    if (!connected || !POLLING_STATUSES.includes(status)) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refreshDomain();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [connected, status, refreshDomain]);

  const onSlugStatus = useCallback((next) => setSlugStatus(next), []);

  const normalized = normalizeDomain(draftDomain);
  const draftKind = classifyDomain(normalized);
  const draftInvalid = Boolean(draftDomain.trim()) && !isValidDomain(normalized);
  const slugDirty = slug !== currentSlug;
  const slugBlocked = ["checking", "taken", "invalid"].includes(slugStatus.state) || slugStatus.slug !== slug;

  const records = domain?.records?.length
    ? domain.records
    : expectedRecords(domain?.domain || "", { platformHost });

  const wwwRecords = records.filter((record) => record.type === "CNAME" && record.name === "www" && domain?.domain_kind !== "subdomain");

  const saveDomain = async () => {
    const saved = await setDomain(normalized);
    if (saved) {
      setEditing(false);
      setDraftDomain("");
    }
  };

  const remove = async () => {
    const removed = await removeDomain();
    if (removed) setConfirmRemove(false);
  };

  const saveSlug = async () => {
    const saved = await patch({ platform_slug: slug }, "Website address updated");
    if (saved) refreshDomain();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
          <MDTypography variant="h6">PearlCRM address</MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            Every website has a free PearlCRM address. It keeps working after you connect your own domain.
          </MDTypography>
          {platformHost && (
            <MDBox display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
              <MDTypography variant="button" fontWeight="medium" sx={{ wordBreak: "break-all" }}>{platformHost}</MDTypography>
              <MDButton size="small" variant="text" color="info" onClick={() => window.open(`https://${platformHost}`, "_blank", "noopener,noreferrer")}>
                Open <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
              </MDButton>
            </MDBox>
          )}
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <SlugField value={slug} onChange={setSlug} onStatus={onSlugStatus} checkSlug={checkSlugForSite} currentSlug={currentSlug} disabled={saving} />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDButton color="info" variant="outlined" disabled={!slugDirty || slugBlocked || saving} onClick={saveSlug} sx={{ mt: 0.5 }}>
                Change address
              </MDButton>
            </Grid>
          </Grid>
          {slugDirty && currentSlug && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              The old address keeps working. If you connected a subdomain with a CNAME, update its target to the new address.
            </MDTypography>
          )}
          {legacyHost && legacyHost !== platformHost && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your earlier address <strong>{legacyHost}</strong> still works. DNS records that point at it keep working, but new
              records should use <strong>{platformHost}</strong>.
            </Alert>
          )}
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <MDBox>
              <MDTypography variant="h6">Custom domain</MDTypography>
              <MDTypography variant="body2" color="text">Serve this website from a domain you own, such as {exampleDomain}.</MDTypography>
            </MDBox>
            {connected && <DomainStatusChip status={status} size="medium" />}
          </MDBox>

          {(!connected || editing) && (
            <MDBox mt={2}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Your domain"
                    placeholder={exampleDomain}
                    value={draftDomain}
                    onChange={(event) => setDraftDomain(event.target.value)}
                    error={draftInvalid}
                    helperText={draftInvalid ? "Enter a domain like www.example.com, without https://." : "A domain or subdomain you own, without https://."}
                  />
                </Grid>
                <Grid item xs={12} md={4} display="flex" gap={1}>
                  <MDButton color="info" disabled={!draftKind || draftInvalid || saving} onClick={saveDomain} sx={{ mt: 0.5 }}>
                    {editing ? "Replace domain" : "Connect domain"}
                  </MDButton>
                  {editing && <MDButton variant="text" color="dark" onClick={() => setEditing(false)} sx={{ mt: 0.5 }}>Cancel</MDButton>}
                </Grid>
              </Grid>
              {draftKind && !draftInvalid && (
                <MDBox mt={2}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>You&apos;ll add these DNS records</MDTypography>
                  <DnsInstructions domain={normalized} domainKind={draftKind} records={expectedRecords(normalized, { platformHost })} pendingText="Shown after you connect" />
                </MDBox>
              )}
            </MDBox>
          )}

          {connected && !editing && (
            <MDBox mt={2}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDTypography variant="h5" sx={{ wordBreak: "break-all" }}>{domain.domain}</MDTypography>
                {domain.domain_kind && (
                  <MDTypography variant="caption" color="text">{domain.domain_kind === "apex" ? "Root domain" : "Subdomain"}</MDTypography>
                )}
                {status === "active" && (
                  <MDButton size="small" variant="text" color="info" onClick={() => window.open(`https://${domain.domain}`, "_blank", "noopener,noreferrer")}>
                    Open <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                  </MDButton>
                )}
              </MDBox>
              <DomainStatusStepper domain={domain} />
              {status === "tls_pending" && (
                <Alert severity="info" sx={{ mb: 2 }}>DNS is set up. We&apos;re issuing an HTTPS certificate, which usually takes a few minutes.</Alert>
              )}
              {status === "failed" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  We stopped checking this domain after repeated failures. Fix the DNS records below, then select Check now.
                </Alert>
              )}
              {domain.last_error && status !== "active" && (
                <Alert severity="warning" sx={{ mb: 2 }}>{domain.last_error}</Alert>
              )}

              {status !== "active" && (
                <>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>DNS records</MDTypography>
                  <DnsInstructions domain={domain.domain} domainKind={domain.domain_kind || classifyDomain(domain.domain)} records={records} pendingText="Refresh to see this value" />
                </>
              )}

              <MDBox mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDButton color="info" disabled={saving} onClick={verifyDomain}>{saving ? "Checking…" : "Check now"}</MDButton>
                <MDButton variant="outlined" color="dark" disabled={saving} onClick={() => { setDraftDomain(domain.domain); setEditing(true); }}>Change domain</MDButton>
                {!confirmRemove ? (
                  <MDButton variant="text" color="error" disabled={saving} onClick={() => setConfirmRemove(true)}>Remove domain</MDButton>
                ) : (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography variant="caption" color="error">Visitors will no longer reach this website at {domain.domain}.</MDTypography>
                    <MDButton size="small" color="error" disabled={saving} onClick={remove}>Remove</MDButton>
                    <MDButton size="small" variant="text" color="dark" onClick={() => setConfirmRemove(false)}>Keep</MDButton>
                  </MDBox>
                )}
              </MDBox>
              <MDTypography variant="caption" color="text" display="block" mt={1}>
                {domain.last_checked_at ? `Last checked ${formatTimestamp(domain.last_checked_at)}.` : "Not checked yet."}
                {POLLING_STATUSES.includes(status) ? " We check automatically every few minutes; this page refreshes every 30 seconds." : ""}
              </MDTypography>
              {status !== "active" && <CheckResults checks={domain.checks} />}
              {status === "active" && domain.www_hostname && (
                domain.www_status === "active" ? (
                  <MDBox display="flex" alignItems="center" gap={1} mt={2}>
                    <CheckCircleIcon fontSize="small" sx={{ color: colors.success.main }} />
                    <MDTypography variant="caption" color="text">
                      {domain.www_hostname} redirects visitors to {domain.domain}.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox mt={2}>
                    <Alert severity={domain.www_status === "tls_pending" ? "info" : "warning"} sx={{ mb: 2 }}>
                      {domain.www_status === "tls_pending"
                        ? `The www record is in place. We're securing ${domain.www_hostname} with HTTPS, which usually takes a few minutes.`
                        : `Add the www record so visitors who type ${domain.www_hostname} reach your website too.`}
                    </Alert>
                    {domain.www_status !== "tls_pending" && wwwRecords.length > 0 && (
                      <DnsInstructions domain={domain.domain} domainKind="apex" records={wwwRecords} pendingText="Refresh to see this value" />
                    )}
                  </MDBox>
                )
              )}
              {status === "active" && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="caption" color="text" display="block">
                    Keep the DNS records in place. Removing them takes the website offline at this domain.
                  </MDTypography>
                </>
              )}
            </MDBox>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

DomainTab.propTypes = {
  site: PropTypes.object.isRequired,
  domain: PropTypes.object,
  saving: PropTypes.bool.isRequired,
  setDomain: PropTypes.func.isRequired,
  verifyDomain: PropTypes.func.isRequired,
  removeDomain: PropTypes.func.isRequired,
  refreshDomain: PropTypes.func.isRequired,
  patch: PropTypes.func.isRequired,
  checkSlug: PropTypes.func.isRequired,
};

DomainTab.defaultProps = { domain: null };

export default DomainTab;
